import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertTriangle, TrendingUp, Users, CheckCircle2 } from "lucide-react";

export default function ProblemSolution() {
  const problems = [
    {
      icon: AlertTriangle,
      title: "Safety Concerns",
      desc: "Unverified drivers and lack of real-time tracking create security risks for employees."
    },
    {
      icon: TrendingUp,
      title: "Rising Costs",
      desc: "Inefficient routing and fuel wastage lead to unpredictable transportation expenses."
    },
    {
      icon: Users,
      title: "Admin Burden",
      desc: "Manual rostering and vendor management consume valuable HR hours."
    }
  ];

  return (
    <section id="solutions" className="py-24 bg-background relative overflow-hidden">
      <div className="container mx-auto px-6">
        
        {/* The Problem */}
        <div className="mb-24">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">The Challenge of Corporate Mobility</h2>
            <p className="text-lg text-muted-foreground">
              Managing employee transport internally is complex, costly, and risky. Traditional models are failing to meet modern workforce expectations.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {problems.map((item, idx) => (
              <Card key={idx} className="border-none shadow-lg bg-white dark:bg-card hover:-translate-y-1 transition-transform duration-300">
                <CardHeader>
                  <div className="w-14 h-14 rounded-xl bg-red-50 dark:bg-red-900/20 flex items-center justify-center mb-4">
                    <item.icon className="h-7 w-7 text-red-500" />
                  </div>
                  <CardTitle className="text-xl font-bold text-primary">{item.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground leading-relaxed">
                    {item.desc}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* The Solution */}
        <div className="relative bg-primary rounded-3xl p-8 md:p-16 overflow-hidden">
          <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-secondary/10 to-transparent pointer-events-none" />
          
          <div className="grid lg:grid-cols-2 gap-12 items-center relative z-10">
            <div className="space-y-8">
              <h2 className="text-3xl md:text-4xl font-bold text-white">
                Enter <span className="text-secondary">Managed Transport as a Service</span>
              </h2>
              <p className="text-lg text-white/80 leading-relaxed">
                We take full ownership of your employee transport operations. From fleet procurement to daily roster management, we deliver a seamless, technology-driven experience.
              </p>
              
              <ul className="space-y-4">
                {[
                  "End-to-End Vendor Management",
                  "AI-Powered Route Optimization",
                  "24/7 Command Center Support",
                  "Automated Billing & Compliance"
                ].map((feature, i) => (
                  <li key={i} className="flex items-center gap-3 text-white">
                    <CheckCircle2 className="h-6 w-6 text-secondary flex-shrink-0" />
                    <span className="font-medium">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="relative">
               <img 
                 src="https://images.pexels.com/photos/1181339/pexels-photo-1181339.jpeg" 
                 alt="Happy Professionals" 
                 className="rounded-2xl shadow-2xl border-4 border-white/10 transform rotate-2 hover:rotate-0 transition-transform duration-500"
               />
               <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-xl max-w-xs hidden md:block">
                 <div className="flex items-center gap-4">
                   <div className="text-4xl font-bold text-primary">10%</div>
                   <div className="text-sm text-muted-foreground font-medium">Average Cost Savings for Clients</div>
                 </div>
               </div>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}
